import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Circle2d } from '@/editor/math/circle2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '../../math/Line2d';
import { LineSeg2d } from '../../math/LineSeg2d';
import { Point2dUtils } from '../../math/Point2dUtils';
import { arcThresholdValue, thresholdValue } from '@/editor/math/const';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { isTangentShape } from '@/editor/math/shape/other';
import { getNearestPoint } from '@/editor/math/utils';
import { IShapePoint2d, IShapeSeg2d } from '@/editor/types/shape';
import { IWallInfo } from '@/editor/types/wallShape';
import { HandleType } from '../wallShape/calculateHelper';
import {
  bodyDirectionOf,
  endOf,
  extendSeg,
  farEndOf,
  isInsideBand,
  markCapArcs,
  nearOffsetSegOf,
  outwardDirectionAt,
  projectAlong,
  pushCapPoint,
  signedSideOfCenterline,
  strictlyOnSeg,
  sweepOnArc
} from './geometry';
import { connectToPointOf, IWallLookup, onWallOf } from './wallLookup';

// 多条弧形隔墙在共享端点处相接(相切或相交)的端盖，按几何确定性求解(通用求交在这种共点退化几何下会选到对方弧远端的幻影点)：
//   · 接头落在房间墙上：第一条(最先建)不被切，平口连到墙边界；其余按「先绘制切后绘制」被所有更早建立的弧切(取最深交点)。
//   · 接头浮空(不在房间墙上)：前两条(按建立序)互相按角平分线斜接(取离接头最近的交点)；第三条及以后被更早建立的弧切(最深)。
//   · 仅一侧偏移弧被切到时，端盖经过中线接头点成 3 点折线；两侧都切到则两点直连。含更早直隔墙的混合接头交回通用逻辑。
export function calArcJunctionCap(
  lookup: IWallLookup,
  mainWallInfo: IWallInfo,
  type: HandleType
): IShapePoint2d[] | null {
  if (!(mainWallInfo.shape instanceof Circle2d) || !(mainWallInfo.seg instanceof ArcSeg2d)) return null;
  const onWall = onWallOf(mainWallInfo, type);
  // 建立先后用真实建立顺序判定(_buildOrder，来自 _wallInfos 下标)，不受缓存插入顺序影响——
  // 房间墙先入缓存时会把相连弧按非建立顺序拉进缓存，用缓存顺序会把最先建的弧误判为"后建"而错切它。
  const mainOrder = lookup.orderOf(mainWallInfo.id);
  const connectedInfos = lookup.resolve(connectToPointOf(mainWallInfo, type));
  // 接头端点落在其 body 上的、更早建立的有厚隔墙(搭接在先画墙身上，非端点相连)。
  const earlierThickOnBody = lookup.resolve(onWall).filter((w) => w.thickness > 0 && lookup.orderOf(w.id) < mainOrder);

  // 与本弧「共用另一端点」的直隔墙是本弧的弦墙(如"盒子+穹顶"里穹顶的底弦)：它横跨整条弧，两条偏移边在本弧的
  // 两个端点附近都与本弧偏移弧相交。它绝不该参与本端点端盖的裁剪——否则 deepestOnSegs 会选到它在【对侧端点】处
  // 扫角最深的那个交点，把本端盖算成关于弧对称的另一端(内外弧凸向随之翻转)。这类弦墙配置由 calPointPointConnected
  // 的孪生近边平切分支专门处理，此处应排除，交回上层保留平切结果。(inserter 分支原已按 oppositeEnd 手工排除。)
  const oppositeEndpoint = farEndOf(mainWallInfo.seg, type);
  const sharesOppositeEndpoint = (w: IWallInfo) =>
    Point2dUtils.equals(w.seg.start, oppositeEndpoint) || Point2dUtils.equals(w.seg.end, oppositeEndpoint);
  // 接头处连着的「更早建立的直隔墙」(厚>0、非圆)。这类接头有两种情形，靠「本弧偏移带是否探入直墙带」区分(见下方处理)：
  //   · 相切延续(如 arcStraightTangentSep)：弧几乎沿切线、偏移边与直墙带平行、探入≈0 → 交回通用逻辑走平口端盖；
  //   · 弧体压进直墙(如半圆弧与竖直直墙相切)：偏移带确实探入直墙带 → 本弧(后建)被该直墙(先建)沿近边裁回。
  const earlierStraightSeps = connectedInfos.filter(
    (w) =>
      w.thickness > 0 &&
      !(w.shape instanceof Circle2d) &&
      lookup.orderOf(w.id) < mainOrder &&
      !sharesOppositeEndpoint(w)
  );
  // 后建的直隔墙(厚>0、非圆、建立序在本弧之后)。用于「本弧是接头第一条时，与真正的第二条(可能是直墙)互相 miter」——
  // 否则第一条弧只在后建弧里找 miter 对象，会跳过更早建立的后建直墙，与错误的墙斜接。
  const laterStraightSeps = connectedInfos.filter(
    (w) => w.thickness > 0 && !(w.shape instanceof Circle2d) && lookup.orderOf(w.id) > mainOrder
  );

  // 共享端点处相连的另一条弧形隔墙(相切或相交都算)。
  const connectedArcs = connectedInfos.filter((w) => w.thickness > 0 && w.shape instanceof Circle2d);
  // 既无相连弧、又无更早直隔墙可裁 → 非本方法负责的接头，交回通用逻辑。
  if (!connectedArcs.length && !earlierStraightSeps.length) return null;
  const earlierArcs = connectedArcs.filter((w) => lookup.orderOf(w.id) < mainOrder);
  const laterArcs = connectedArcs.filter((w) => lookup.orderOf(w.id) > mainOrder);
  // 与本弧「共用另一端点」的更早弧(和本弧一起构成透镜/弦——两端点都相接，如 arc2/arc3 都连 J 与 K)不能当端盖 cutter：
  // 它的偏移弧在本弧【对侧端点】附近也与本弧偏移弧相交，deepestOnSegs 会选到那个对侧交点，把端盖算到弧的另一头
  // (与直墙弦 earlierStraightSeps 的 sharesOppositeEndpoint 同理)。仍保留在 earlierArcs 里参与「第几条」计数
  // (前两条 miter / 第三条+ 被切),仅在裁切取点时用本集合排除。
  // 例外：本墙在该端点的偏移端点若确实【埋在】透镜伙伴的墙体带里(不只是在端点处相掠)，说明本墙这一头压在它身上，
  // 必须由它来裁——否则本墙只被接头处另一条墙浅浅削掉几毫米、和它大面积交叠(看起来就是"第三条没被前两条切")。
  // 排除本身只是为了躲开「选到对侧端点交点」的老毛病，而 deepestOnSegs 已改为每条切割边取【近接头出口】，
  // 对侧那次相交不会再被选中，所以这里可以按"是否真埋进去"放行。
  const mainOffsetEndsAtJ = [endOf(mainWallInfo.leftSeg, type), endOf(mainWallInfo.rightSeg, type)];
  const buriedInBandOf = (w: IWallInfo) => mainOffsetEndsAtJ.some((p) => isInsideBand(p, w.seg, w.thickness / 2));
  const earlierCutterArcs = earlierArcs.filter((w) => !sharesOppositeEndpoint(w) || buriedInBandOf(w));

  const mainOuterIsLeft = (mainWallInfo.leftSeg as ArcSeg2d).radius >= (mainWallInfo.rightSeg as ArcSeg2d).radius;
  const mainOuterSeg = (mainOuterIsLeft ? mainWallInfo.leftSeg : mainWallInfo.rightSeg) as ArcSeg2d;
  const mainInnerSeg = (mainOuterIsLeft ? mainWallInfo.rightSeg : mainWallInfo.leftSeg) as ArcSeg2d;
  const orderLR = (outer: IShapePoint2d, inner: IShapePoint2d) => (mainOuterIsLeft ? [outer, inner] : [inner, outer]);
  const onRoomWall = lookup.resolve(onWall).some((w) => w.thickness === 0);

  const junction = endOf(mainWallInfo.seg, type);
  const leftEnd = endOf(mainWallInfo.leftSeg, type);
  const rightEnd = endOf(mainWallInfo.rightSeg, type);

  // 本弧与相邻弧 other 的「角平分线斜接」：左右偏移边各自与 other 对应侧偏移边求交，取离接头最近的交点(够不到则 null)。
  // 对应侧的配对(共起点时左右互换)沿用 calLeftRightIntersection 的判据，避免大弧远端幻影(getNearestPoint 取近接头点)。
  // 交点是否落在给定偏移弧的实际角度范围内(带小容差)。
  const onArcRange = (seg: IShapeSeg2d, p: IPoint2d): boolean =>
    !(seg instanceof ArcSeg2d) || sweepOnArc(seg, p, 0.05) !== null;
  // 斜接取点：先用距离闸(≤6×墙厚)排除大弧远端的幻影交点(两弧只应在接头附近斜接)，剩下的优先落在本偏移弧实际弧段内的
  // 交点，再取离接头最近的。关键：过滤后为空要退回「距离闸内的近点池」而非全部——尖楔 miter 的合法尖点会略超出弧段
  // 标称角度范围(如接头前一点点)，若只认弧段内点就会漏掉它、退回远端幻影，端盖飞到大弧另一头并使轮廓自交。
  const maxMiter = mainWallInfo.thickness * 6;
  const pickMiter = (pts: IPoint2d[] | null | undefined, mainSeg: IShapeSeg2d): IShapePoint2d | null => {
    if (!pts || !pts.length) return null;
    const near = pts.filter((p) => Point2dUtils.distanceTo(p, junction) <= maxMiter);
    const pool = near.length ? near : pts;
    const onArc = pool.filter((p) => onArcRange(mainSeg, p));
    return getNearestPoint(onArc.length ? onArc : pool, junction);
  };
  const miterAgainst = (other: IWallInfo): { left: IShapePoint2d | null; right: IShapePoint2d | null } => {
    const isStartEnd =
      Point2dUtils.equals(other.seg.start, mainWallInfo.seg.end) ||
      Point2dUtils.equals(other.seg.end, mainWallInfo.seg.start);
    let oLeft = other.leftShape;
    let oRight = other.rightShape;
    if (!isStartEnd) [oLeft, oRight] = [oRight, oLeft];
    const lPts = intersectCommonShape(mainWallInfo.leftShape, oLeft);
    const rPts = intersectCommonShape(mainWallInfo.rightShape, oRight);
    return {
      left: pickMiter(lPts, mainWallInfo.leftSeg),
      right: pickMiter(rPts, mainWallInfo.rightSeg)
    };
  };

  const junctionBand = 0.01;
  // 求本偏移弧(整圆)与给定边(弧→整圆 / 线→无限直线)的交点，过滤到该边实际范围 + 本弧起点向内的扫角，返回按 selector 选出的一个。
  const crossSeg = (
    mainOffset: ArcSeg2d,
    seg: IShapeSeg2d,
    selector: (sweep: number, prevSweep: number) => boolean
  ): { point: IPoint2d; sweep: number } | null => {
    const mainCircle = mainOffset.createCircle();
    const totalSweep = mainOffset.getSweepAngle();
    const isArc = seg instanceof ArcSeg2d;
    const ext = extendSeg(seg);
    const ips = intersectCommonShape(mainCircle, ext, arcThresholdValue);
    if (!ips) return null;
    let best: { point: IPoint2d; sweep: number } | null = null;
    for (const p of ips) {
      const sFromArcStart = mainOffset.getPointSweepAngle(p);
      let sweep = type === HandleType.Start ? sFromArcStart : totalSweep - sFromArcStart;
      if (sweep > Math.PI) sweep -= 2 * Math.PI; // 起点略后侧的回卷 → 小负值
      // 起点略后侧的允许量同样要有【绝对长度】上限(与下面 cutter 侧的 eTol 对称)：junctionBand 是固定角度，
      // r=20000 时 0.01rad 已是 200mm，会把远在本弧起点之外的交点当成"就在接头处"。
      const sTol = Math.min(junctionBand, mainWallInfo.thickness / mainOffset.radius);
      if (sweep < -sTol || sweep > totalSweep + arcThresholdValue) continue;
      if (isArc) {
        const cutter = seg;
        let eSweep = cutter.getPointSweepAngle(p);
        if (eSweep > 2 * Math.PI - junctionBand) eSweep -= 2 * Math.PI;
        // 弧段外的容差同样要有【绝对长度】上限(1×墙厚，斜接/端盖那一小圈)：junctionBand 是固定角度，
        // 半径越大它对应的弧长越大(r=10000 时 0.01rad 就是 100mm)，会把远在弧段之外的交点当成"在这条边上"。
        // 取两者更紧的那个，只收紧不放宽(实测既有用例的最大越界 0.86×墙厚，仍在闸内)。
        const eTol = Math.min(junctionBand, mainWallInfo.thickness / cutter.radius);
        if (eSweep < -eTol || eSweep > cutter.getSweepAngle() + eTol) continue;
      } else {
        const dx = seg.end.x - seg.start.x;
        const dy = seg.end.y - seg.start.y;
        const len2 = dx * dx + dy * dy;
        const len = Math.sqrt(len2);
        const t = len2 > 0 ? ((p.x - seg.start.x) * dx + (p.y - seg.start.y) * dy) / len2 : 0;
        // 端点外的容差要按【绝对长度】给(半个墙厚,即斜接/端盖那一小圈)，不能按比例。
        // 用 ±2% 时长墙会失控：7000 长的直隔墙 2% 就是 140mm，远在它端点【之外】的延长线交点会被当成"在这条边上"，
        // 端盖于是切到墙外的空地上(表现为最后一条弧在接头处甩出一条一两百长的尖刺)。
        const tTol = len > 0 ? (mainWallInfo.thickness * 0.5) / len : 0;
        if (t < -tTol || t > 1 + tTol) continue;
      }
      if (!best || selector(sweep, best.sweep)) best = { point: p, sweep };
    }
    return best;
  };

  // 更早那几条墙用于切割的边：外侧偏移弧用两条偏移边(内/外)都参与；内侧偏移弧只用「更早弧的内偏移弧」。
  // 依据：本弧内轮廓要交到更早弧的内弧(见多弧扇形共点用例)——若把更早弧外偏移也纳入内侧候选，
  // 会误取到更深但方向不对的外偏移交点，端盖内切点错误。
  const innerOf = (w: IWallInfo) =>
    (w.leftSeg as ArcSeg2d).radius <= (w.rightSeg as ArcSeg2d).radius ? w.leftSeg : w.rightSeg;
  const cutterSegsOf = (cutters: IWallInfo[]): { seg: IShapeSeg2d; wallId: string }[] => {
    const segs: { seg: IShapeSeg2d; wallId: string }[] = [];
    for (const w of cutters) segs.push({ seg: w.leftSeg, wallId: w.id }, { seg: w.rightSeg, wallId: w.id });
    // 再补上最早两条斜接角把轮廓填到接头之外的那几段延长(jointFillSegs，见其定义)。
    for (const f of jointFillSegs)
      if (cutters.some((w) => w.id === f.wallId)) segs.push({ seg: f.seg, wallId: f.wallId });
    return segs;
  };
  const innerCutterSegsOf = (cutters: IWallInfo[]): { seg: IShapeSeg2d; wallId: string }[] => {
    const segs = cutters.map((w) => ({ seg: innerOf(w), wallId: w.id }));
    for (const f of jointFillSegs)
      if (f.inner && cutters.some((w) => w.id === f.wallId)) segs.push({ seg: f.seg, wallId: f.wallId });
    return segs;
  };

  const deeper = (a: number, b: number) => a > b;
  // 每条切割边上取「离接头最近(扫角最小)」的那个界内交点——即本偏移弧自接头出发第一次穿出该切割边所在带的出口。
  // 关键：两条弧的偏移弧可能相交【两次】(近接头一次、绕到大弧远端再一次)。junction cap 只该用近接头那次；
  // 若按「最深(扫角最大)」取，会选到远端那次(该点常恰落在切割弧的另一端点上)，把端盖拉到大弧远头、与倒数第二条弧的
  // 终点轮廓交叉(用户反馈的最后一条弧轮廓问题)。单次相交时最近=唯一,与原逻辑一致,不影响既有扇形/多弧用例。
  const nearer = (a: number, b: number) => a < b;
  // 本偏移弧被给定切割边中「切得最深(各边取近接头出口后、离本端点扫角最大)」的那条切割，返回交点、所在边及所属墙。
  // 各边先取近接头出口(见 nearer),再在这些出口里取最深的 → 多条更早墙重叠成的「平台」的并集出口。
  // minSweep：只保留扫角严格大于它的交点(用于内偏移「优先真实切入更早弧内弧」，排除接头处扫角≈0/负的假掠过)。
  // 本墙某条偏移弧上「离接头 sweepFromJunction(弧度)」处的点：接头是 start 时沿扫向前进，是 end 时反向。
  const pointAtSweep = (arc: ArcSeg2d, sweepFromJunction: number): IPoint2d => {
    const s = type === HandleType.Start ? sweepFromJunction : arc.getSweepAngle() - sweepFromJunction;
    const a = arc.clockwise ? arc.startAngle - s : arc.startAngle + s;
    return { x: arc.center.x + arc.radius * Math.cos(a), y: arc.center.y + arc.radius * Math.sin(a) };
  };

  const deepestOnSegs = (
    mainOffset: ArcSeg2d,
    cutterSegs: { seg: IShapeSeg2d; wallId: string }[],
    minSweep = -Infinity
  ): { point: IPoint2d; seg: IShapeSeg2d; wallId: string } | null => {
    let best: { point: IPoint2d; seg: IShapeSeg2d; wallId: string; sweep: number } | null = null;
    for (const { seg, wallId } of cutterSegs) {
      const hit = crossSeg(mainOffset, seg, nearer);
      if (hit && fillSegSet.has(seg) && !strictlyOnSeg(seg, hit.point)) continue;
      // 切点若【严格埋在另一条更早墙的墙体里】，那里本墙的轮廓还没露出来——它不可能是可见的出口，跳过、继续找更深的。
      // (buriedByEarlier 用 isInsideBand：落在某墙【边线上】不算埋进去，所以正常的切点/并集角不会被误杀。)
      // 用户反馈"最后一条的终点轮廓没有被前两条裁切"：实测内偏移取到的切点落在更早弧 A 的偏移弧【端点外】32.8
      // (被 crossSeg 的容差放进来)、且还深埋在更早弧 B 的带里 28.3，按它切等于没切，轮廓直接压过 B 的墙体；
      // 真正的出口在沿本弧再往里 99 处、落在 B 的外偏移弧上。
      // 切点必须是这条偏移边真正的【出口】：沿本墙再往里探 0.5×墙厚，若那里还埋在某条更早墙的墙体里，说明这条偏移边
      // 还会从那条墙里再钻出来——按此点切等于没被裁到，之后那一段轮廓会压在更早墙的墙体上(用户反馈"最后一条的终点
      // 轮廓没有被前两条裁切")。实测该场景：内偏移取到的点在更早弧 A 的偏移弧端点外 32.8、且深埋在更早弧 B 的带里 28.3，
      // 真正的出口在再往里 66 处、落在 B 的外偏移弧上。注意只看「探针点」而不是切点本身：切点落在别的墙体【内部】是允许的
      // (那一段藏在那条墙里、看不见)，只要本墙从这里起确实不再露出来。
      // 例外：切点本身就在本墙自己的偏移端点上(沿本墙几乎没削掉东西，如与更早墙相切、平口贴齐时的相掠接触)——
      // 那不是"要不要在这里切"的问题，探针判据不适用(相切并行时探针必然还在邻墙带里，会把正常的贴齐平口误判掉)。
      // 探针要【两段】看，只看 0.5×墙厚那一个点会一步跨过"还埋着的那一小截"：实测三墙接头(穹顶+弦+内拱)处，
      // 本弧内偏移进了弦墙 3.9mm 就与另一条更早弧的偏移圆相交，而它还要再走 31mm 才穿出弦墙 —— 单点探针一步
      // 跨了 35mm 落在弦墙外，于是把那个交点当成出口，端盖从弦墙肚子里起笔、横穿它的上边线(轮廓自检报两墙互穿)。
      // 半程(0.25×墙厚)也要清爽才算真出口。为什么容忍更短的那一截：多墙扇形接头里，端盖会【故意】取邻墙上更浅的
      // 那个点再经并集角折过去填三角缝，那个点后面常还压在另一条更早墙里一小段(实测 0.15×墙厚)，那是隐藏在
      // 对方墙体内、看不见的一截，不能按"没穿出"否掉。两者量级分得很开(0.15 vs 0.4 墙厚)。
      const probeSpan = (mainWallInfo.thickness * 0.5) / mainOffset.radius;
      const buriedAhead = (sweep: number) =>
        buriedByEarlier(pointAtSweep(mainOffset, sweep + probeSpan / 2)) ||
        buriedByEarlier(pointAtSweep(mainOffset, sweep + probeSpan));
      if (hit && hit.sweep * mainOffset.radius > mainWallInfo.thickness * 0.02 && buriedAhead(hit.sweep)) {
        continue;
      }
      if (hit && hit.sweep > minSweep && (!best || hit.sweep > best.sweep))
        best = { point: hit.point, seg, wallId, sweep: hit.sweep };
    }
    return best ? { point: best.point, seg: best.seg, wallId: best.wallId } : null;
  };

  // 点是否被某条更早的相连墙实心带盖住(含被排除出 cutter 的透镜伙伴/弦墙——判「有没有被盖住」要算上它的墙体)。
  const buriedByEarlier = (p: IPoint2d) =>
    connectedInfos.some(
      (w) => w.thickness > 0 && lookup.orderOf(w.id) < mainOrder && isInsideBand(p, w.seg, w.thickness / 2)
    );
  // 点是否落在「接头处【最早那两条】墙互相斜接后、越过接头填出的实心角」里——band 的扫角范围到接头就截止了，
  // buriedByEarlier 看不到这块斜接角。只看最早两条：接头处只有这两条互相 miter(端盖可以越过接头)，第三条起
  // 都是被切/贴着接头收回来的，填不出接头另一侧的墙体。判据复用 miterAgainst 的左右配对与 pickMiter 的距离闸：
  //   · 两侧都斜接得到 → 这对墙的端盖是【跨过接头】的一条弦(接头两侧都被填实)，p 在弦的墙体侧即被盖住
  //     (如 threeArcFanNoPhantom 的 pfq0×RqBr8 成 115°角相接，反侧斜接角距接头 89>半墙厚)；
  //   · 只有一侧斜接得到(两墙在接头近乎同向/相切时另一侧的偏移边根本不相交) → 两墙端盖都经过接头、
  //     另一侧是敞开的 V 型豁口，落在豁口里的 p 没被盖住(sixArcFan/本用例的窄楔接头)。
  // 还要求 p 至少落在其中一条墙的带宽内(不看扫角)，否则谈不上被这对墙盖住。
  const firstTwoThick = connectedInfos
    .filter((w) => w.thickness > 0 && lookup.orderOf(w.id) < mainOrder)
    .sort((a, b) => lookup.orderOf(a.id) - lookup.orderOf(b.id))
    .slice(0, 2);
  // 最早两条墙互相斜接出的两个斜接点(左侧对/右侧对各一个)。求不到就是 V 型豁口(两墙近乎同向/相切)。
  const jointMiters = ((): {
    a: IWallInfo;
    b: IWallInfo;
    isStartEnd: boolean;
    mLeft: IPoint2d;
    mRight: IPoint2d;
  } | null => {
    if (firstTwoThick.length < 2) return null;
    const [a, b] = firstTwoThick;
    const pairMiter = (s1: Circle2d | Line2d, s2: Circle2d | Line2d): IPoint2d | null => {
      const ips = intersectCommonShape(s1, s2);
      const near = (ips || []).filter((q) => {
        const d = Point2dUtils.distanceTo(q, junction);
        return d > thresholdValue && d <= maxMiter;
      });
      return near.length ? getNearestPoint(near, junction) : null;
    };
    const isStartEnd = Point2dUtils.equals(b.seg.start, a.seg.end) || Point2dUtils.equals(b.seg.end, a.seg.start);
    const mLeft = pairMiter(extendSeg(a.leftSeg), extendSeg(isStartEnd ? b.leftSeg : b.rightSeg));
    const mRight = pairMiter(extendSeg(a.rightSeg), extendSeg(isStartEnd ? b.rightSeg : b.leftSeg));
    return mLeft && mRight ? { a, b, isStartEnd, mLeft, mRight } : null;
  })();
  // 斜接角把墙体填到了接头【之外】(直到斜接点)，那两段「偏移边的延长」同样是并集轮廓的一部分，必须当 cutter 边。
  // 否则后画墙埋在填充区里的那侧偏移根本不会被裁，它的轮廓会直接穿过前两条的边界(交叉)。
  // 每段都是有限的：从该偏移边在接头的端点，延长到这一侧的斜接点为止。
  const jointFillSegs = ((): { seg: IShapeSeg2d; wallId: string; inner: boolean }[] => {
    const out: { seg: IShapeSeg2d; wallId: string; inner: boolean }[] = [];
    if (!jointMiters) return out;
    const { a, b, isStartEnd, mLeft, mRight } = jointMiters;
    // 两个斜接点里只有【反侧(凸角)】那个才是把墙体填到接头之外的；另一个在两墙夹角的凹槽里，
    // 那一侧的"延长段"其实躺在对方墙体【内部】，不是轮廓，拿它当 cutter 会把后画墙切错。
    // 判据：凸角在两墙 body 的反方向上(与 body 方向之和的点积为负)。
    const bodyOf = (w: IWallInfo): IPoint2d => outwardDirectionAt(w.seg, junction) ?? { x: 0, y: 0 };
    const bodySum = (() => {
      const da = bodyOf(a);
      const db = bodyOf(b);
      return { x: da.x + db.x, y: da.y + db.y };
    })();
    const isOuterCorner = (m: IPoint2d) => (m.x - junction.x) * bodySum.x + (m.y - junction.y) * bodySum.y < 0;
    const sides = (
      [
        [a.leftSeg, isStartEnd ? b.leftSeg : b.rightSeg, mLeft],
        [a.rightSeg, isStartEnd ? b.rightSeg : b.leftSeg, mRight]
      ] as [IShapeSeg2d, IShapeSeg2d, IPoint2d][]
    ).filter(([, , m]) => isOuterCorner(m));
    for (const [sa, sb, m] of sides) {
      for (const [w, s] of [
        [a, sa],
        [b, sb]
      ] as [IWallInfo, IShapeSeg2d][]) {
        const atStart = Point2dUtils.equals(w.seg.start, junction);
        const endAtJ = atStart ? s.start : s.end;
        if (Point2dUtils.distanceTo(endAtJ, m) <= thresholdValue) continue;
        const inner = innerOf(w) === s;
        if (s instanceof ArcSeg2d) {
          // 延长段与本墙扫向同/反：接头是 end 时继续往前扫，是 start 时往回扫。
          out.push({
            seg: new ArcSeg2d({
              start: endAtJ,
              end: m,
              center: s.center,
              clockwise: atStart ? !s.clockwise : s.clockwise
            }),
            wallId: w.id,
            inner
          });
        } else {
          out.push({ seg: new LineSeg2d(endAtJ, { x: m.x, y: m.y }), wallId: w.id, inner });
        }
      }
    }
    return out;
  })();
  // 这些"延长段"是精确边界(端点就是斜接点)，没有端盖那一圈松动余量：取点时必须【严格】落在段内，
  // 否则 crossSeg 的容差(可达 0.01rad≈28mm)会让交点越过斜接点、切到填充区之外。
  const fillSegSet = new Set(jointFillSegs.map((f) => f.seg));
  const jointFillCovers = (p: IPoint2d): boolean => {
    if (!jointMiters) return false;
    const { a, b, mLeft, mRight } = jointMiters;
    const inWidth = (w: IWallInfo) =>
      Math.abs(signedSideOfCenterline(p, extendSeg(w.seg))) < w.thickness / 2 - thresholdValue;
    if (!inWidth(a) && !inWidth(b)) return false;
    const chord = new Line2d({ points: { p1: mLeft, p2: mRight } });
    // 墙体侧参考点：沿 a 在接头处的切向(指向 a 本体)偏出一个墙厚，必在 a 的实心里。
    const tA = Point2dUtils.getUnitDirection(
      junction,
      Point2dUtils.equals(a.seg.start, junction) ? a.seg.end : a.seg.start
    );
    const ref = { x: junction.x + tA.x * a.thickness, y: junction.y + tA.y * a.thickness };
    const sp = signedSideOfCenterline(p, chord);
    const sr = signedSideOfCenterline(ref, chord);
    // 在弦的墙体侧 = 被填实的角盖住；只在弦【外侧】≤0.3×墙厚时也算(等于贴在这对墙的端盖面上，切齐即可、不会露缝；
    // 弦是斜接角的直线近似，本身有这个量级的误差)。V 型豁口(求不到弦)时上面已经 return false，不受此容差影响。
    return sp * sr > 0 || Math.abs(sp) <= mainWallInfo.thickness * 0.3;
  };
  // 内偏移这一侧「保持原有长度」的阶梯端盖：外偏移交点 →(沿被切的那条更早轮廓边)→ 它靠接头的端点
  // →(沿更早墙在接头的墙厚端盖面)→ 接头 J → 自身内偏移端点。
  // 用于内偏移端点根本没被任何更早墙盖住(落在开放空间里)的情形：这一侧不能被切回去——那会凭空削掉一截没被任何
  // 东西挡住的墙体、端盖与接头之间留出一道缝。
  // grazeLimit：本弧【近乎沿着接头处那条轮廓面擦出去】时(切向与该面近平行)返回 null 交回调用方。默认 0.88(≈28°)，
  // 但扇形那条调用点放到 0.999——那里的替代方案是「内外同弧度的径向端盖」，它会把没被任何东西挡住的那一侧
  // 凭空削掉 40~47，接头处留出肉眼可见的缺口(用户反馈"后面两条弧在连接处的裁切不太对")，所以宁可折出细长的阶梯。
  // 被切的可能是外偏移、也可能是内偏移(取决于本弧朝哪一侧鼓出接头处的那条轮廓面)，两种都按同一形状阶梯折：
  // 被切侧切点 →(经切割边靠接头的端点，即更早墙在 J 的偏移端点/并集角)→ 接头 J → 另一侧本弧自身的偏移端点。
  const steppedKeepOther = (
    cut: { point: IPoint2d; seg: IShapeSeg2d },
    cutIsOuter: boolean,
    grazeLimit = 0.88
  ): IShapePoint2d[] | null => {
    const nearEnd = getNearestPoint([cut.seg.start, cut.seg.end], junction);
    if (!nearEnd) return null;
    // 本弧在接头处沿 body 前进的切向(bodyDirectionOf 按 start/end 翻转)。不能用「接头→另一端点」的弦方向判：
    // 半圆时弦与切向垂直、扫角>π 时弦方向与切向相反。
    const tangentAtJ = bodyDirectionOf(mainWallInfo.seg, type);
    const faceDir = Point2dUtils.getUnitDirection(junction, nearEnd);
    if (Math.abs(tangentAtJ.x * faceDir.x + tangentAtJ.y * faceDir.y) > grazeLimit) return null;
    const keptEnd = endOf(cutIsOuter ? mainInnerSeg : mainOuterSeg, type);
    const stepped: IShapePoint2d[] = [{ x: cut.point.x, y: cut.point.y }];
    pushCapPoint(stepped, nearEnd, junction, maxMiter);
    pushCapPoint(stepped, junction, junction, maxMiter);
    pushCapPoint(stepped, keptEnd, junction, maxMiter);
    if (Point2dUtils.distanceTo(stepped[stepped.length - 1], keptEnd) > thresholdValue) return null;
    // stepped 的顺序是「被切侧 → 保留侧」；端盖须按 left→right 输出。
    const cutIsLeft = cutIsOuter === mainOuterIsLeft;
    // 切点 → 该切割边靠接头的端点这一段沿的就是 cut.seg：弧则标成弧(排好序后再标，center 描述的是「本点→下一点」)。
    return markCapArcs(cutIsLeft ? stepped : stepped.reverse(), mainWallInfo, cut.seg);
  };
  const steppedKeepInner = (outer: { point: IPoint2d; seg: IShapeSeg2d }, grazeLimit = 0.88) =>
    steppedKeepOther(outer, true, grazeLimit);

  // 第三条及以后：外偏移弧被最深的更早墙边切。
  // 内偏移弧优先「真实切入某条更早弧内弧」(在更早弧内偏移弧上、且扫角严格为正 → 确是切进本弧体内，非接头处假掠过)；
  // 多弧扇形共点时本弧内轮廓应交到更早弧的内弧(用例：三→二内弧、四/五→一内弧)。若无此类正向内切，退回通用「所有更早墙边取最深」。
  //   · 两侧切到同一条墙 → 两点直连；两侧切到不同墙 → 三点，中间插入两切割边的交点(两更早弧的斜接角)。
  const INNER_POS = 1e-3;
  const cutByEarlier = (cutters: IWallInfo[]): IShapePoint2d[] | null => {
    let outerDeep = deepestOnSegs(mainOuterSeg, cutterSegsOf(cutters));
    const innerPos = deepestOnSegs(mainInnerSeg, innerCutterSegsOf(cutters), INNER_POS);
    let innerDeep = innerPos ?? deepestOnSegs(mainInnerSeg, cutterSegsOf(cutters));
    // 【外】偏移这一侧其实没有真交点时先把它清掉(与下面内侧那条判据对称)：
    //   · 切点不【严格】落在切割边的标称范围内 —— 只是 crossSeg 的 junctionBand(0.01rad) 容差在接头处放进来的相掠幻影
    //     (实测本弧外偏移落在更早弧内偏移弧【端点外】0.008rad≈10 处"相交")；
    //   · 按它切确实会削掉东西(>0.02×墙厚)。相切平口贴齐那类接头两侧都只是在【自身偏移端点处】相掠(削掉量恰为 0)，
    //     那是正常的贴齐平口(lastArcTangentFlatCut)，不能当幻影清掉；
    //   · 而自身外偏移端点又在开放空间(既不在任何更早墙的带里、也不在最早两墙跨过接头的斜接角里)。
    // 这一侧没被任何东西挡住，就不能按幻影点切回去(否则凭空削掉一截、还会被误判成"两侧都被切"而丢掉阶梯端盖：
    // 用户反馈"移动最后一条的终点，切割又出问题了"——同一接头换个终点，外偏移就会在更早弧的偏移弧端点外相掠)。
    const outerEndPt = endOf(mainOuterSeg, type);
    if (
      outerDeep &&
      !strictlyOnSeg(outerDeep.seg, outerDeep.point) &&
      Point2dUtils.distanceTo(outerDeep.point, outerEndPt) > mainWallInfo.thickness * 0.02 &&
      !buriedByEarlier(outerEndPt) &&
      !jointFillCovers(outerEndPt)
    ) {
      outerDeep = null;
    }
    // 内偏移这一侧其实【没有真交点】，不该被切回去：
    //   ① 选中的内切点落在切割边标称范围【外】——只是被 crossSeg 的 junctionBand(0.01rad) 容差放进来的相掠幻影
    //      (内偏移优先取的 innerPos 也可能是这种幻影，所以两者都要重判，不能只看回退取点)；
    //   ② 按它切会削掉 >0.15×墙厚 —— 削掉的量在这个量级以下时本侧其实已经与更早轮廓贴齐了，照旧切齐即可，
    //      不必为零点几毫米插一条会自我折返的阶梯(arcLapOnEarlierArcBody/thirdArcNearParallelCutByEarlier)；
    //   ③ 自身内偏移端点既不在任何更早墙的带里、也不在最早两墙跨过接头的斜接角里 —— 它在开放空间。
    // 接头处几条弧近乎相切时这种幻影切点普遍存在，按它切(或按下方同墙补取切)会把没被挡住的墙体削掉一截、端盖与
    // 接头之间留缝——而相邻的后一条弧是按本弧【原长】的偏移弧裁的，两者还对不上。这一侧保持原长、走阶梯端盖。
    const innerEnd = endOf(mainInnerSeg, type);
    if (
      outerDeep &&
      innerDeep &&
      !strictlyOnSeg(innerDeep.seg, innerDeep.point) &&
      Point2dUtils.distanceTo(innerDeep.point, innerEnd) > mainWallInfo.thickness * 0.15 &&
      !buriedByEarlier(innerEnd) &&
      !jointFillCovers(innerEnd)
    ) {
      // 这里的「近平行擦面」闸放到 0.98：本分支的替代方案不是径向端盖而是【按幻影点切】——那本身就是错的，
      // 所以只在几乎完全贴着轮廓面(夹角<11°)时才放弃阶梯。
      const kept = steppedKeepInner(outerDeep, 0.98);
      if (kept) return kept;
    }
    // 内偏移无真正的前向切点(innerPos 为空)、且回退取到的内点落在与外点【不同】的更早墙上时：外点所在的那条更早墙
    // (相邻/倒数第二条)的内偏移弧,其实与本内偏移在接头附近也相交,只因该交点落在它标称弧段外一点点(斜接端盖内、被
    // crossSeg 的紧容差滤掉)而没入选,于是内点错接到另一条更早弧接头后侧的浅交点。此处直接圆-圆求交补取该【同墙】交点
    // (离接头最近、局部尺度内),使内外偏移都对接同一条相邻墙、成干净的角平分线斜接。仅在此退化情形补取,不影响正常扇形
    // (innerPos 非空)或本就同墙的情形。
    if (!innerPos && outerDeep && innerDeep && innerDeep.wallId !== outerDeep.wallId) {
      const adj = cutters.find((w) => w.id === outerDeep.wallId);
      const adjInner = adj ? (innerOf(adj) as ArcSeg2d) : null;
      if (adjInner instanceof ArcSeg2d) {
        const ips = intersectCommonShape(mainInnerSeg.createCircle(), adjInner.createCircle(), arcThresholdValue);
        const reach = Math.max(Point2dUtils.distanceTo(outerDeep.point, junction), mainWallInfo.thickness) * 2;
        // 补取的前提是「真交点只是落在那条偏移弧标称端点外一点点(斜接端盖那一小圈)」——所以它必须【贴着该弧靠接头的
        // 端点】(≤1×墙厚)。这里用的是整圆求交、不受弧段范围约束，没有这道闸时两条近乎相切的墙会在远处交出一个
        // 一两百远的幻影点(实测 2.85×墙厚)，端盖被拉成一条甩到接头外空地上的尖刺。
        const adjNearEnd = getNearestPoint([adjInner.start, adjInner.end], junction);
        const cand = (ips || [])
          .map((p) => ({ p, d: Point2dUtils.distanceTo(p, junction) }))
          .filter(
            (c) =>
              c.d <= reach &&
              (!adjNearEnd || Point2dUtils.distanceTo(c.p, adjNearEnd) <= mainWallInfo.thickness) &&
              // 补取来的点同样要是【真出口】：它若深埋在另一条更早墙的墙体里，本墙从这里起还会从那条墙里钻出来，
              // 按它切等于没被裁到(实测该点在更早弧 A 的偏移弧端点外 32.8、又埋在更早弧 B 的带里 28.3，
              // 真出口在再往里 66 处、落在 B 的外偏移弧上)。
              !buriedByEarlier(c.p)
          )
          .sort((a, b) => a.d - b.d)[0];
        if (cand) innerDeep = { point: { x: cand.p.x, y: cand.p.y }, seg: adjInner, wallId: outerDeep.wallId };
      }
    }
    // 内、外偏移都切到同一条更早墙、且两接触点分居接头两侧(该墙的边从两点之间穿过 → 沿墙方向的投影一正一负)：
    // 此时 cap 弦会横切出两条边界墙的并集、在扇形另一侧留三角缝。内偏移其实面向「另一条边界墙」，应改取那条墙上的
    // 交点(即使较浅)——使 leftDeep/rightDeep 落在不同墙上、cap 经并集角连成 3 点折线、贴合两侧轮廓(内弧延长到与
    // 另一条轮廓相交，平整无缝)。而多弧扇形里内外偏移都真实落在同一条更早弧内弧的同侧(投影同号)，不触发、保持原样。
    if (outerDeep && innerDeep && outerDeep.wallId === innerDeep.wallId) {
      // outerDeep 是 let、别处还会被重新赋值，闭包里读它会丢掉这里的判空收窄，先把 id 取出来
      const sharedWallId = outerDeep.wallId;
      const shared = cutters.find((w) => w.id === sharedWallId);
      const along = shared
        ? Point2dUtils.getUnitDirection(
            junction,
            Point2dUtils.equals(shared.seg.start, junction) ? shared.seg.end : shared.seg.start
          )
        : null;
      const proj = (p: IPoint2d) => (along ? projectAlong(p, junction, along) : 0);
      // 需「一点明显在墙前进方向前方、另一点明显在接头后方」才算真跨越(该墙边从两点之间穿过、留缝)；
      // 阈值按墙厚取(≈0.2×厚)以滤掉多弧扇形里内点恰好落在接头附近(投影≈0、属容差噪声)的合法同墙情形。
      const straddleTol = mainWallInfo.thickness * 0.2;
      if (along && proj(outerDeep.point) > straddleTol && proj(innerDeep.point) < -straddleTol) {
        const other = cutters.filter((w) => w.id !== sharedWallId);
        const innerOther = other.length
          ? (deepestOnSegs(mainInnerSeg, innerCutterSegsOf(other), INNER_POS) ??
            deepestOnSegs(mainInnerSeg, cutterSegsOf(other)))
          : null;
        // 距离闸：延长后的内点必须仍在接头附近的端盖尺度内(≤2×外点到接头的距离、且不小于墙厚量级)。
        // 否则说明「另一条墙」只在大弧远处与内偏移相交——选它会把端盖拉到远端、得到跨越大弧的错误结果，宁可放弃
        // 切换、退回原同墙结果(仅留小缝，也不做错)。
        const maxReach = Math.max(Point2dUtils.distanceTo(outerDeep.point, junction), mainWallInfo.thickness) * 2;
        if (innerOther && Point2dUtils.distanceTo(innerOther.point, junction) <= maxReach) innerDeep = innerOther;
      }
    }
    // 只有外偏移一侧被切(内偏移端点在开放空间、保持原长)时：端盖必须沿更早墙的轮廓【阶梯折】过去——经那条切割边
    // 靠接头的端点(即更早墙在 J 的偏移端点/并集角)再回到接头。直接「切点→接头」两点连的话，这条线会斜穿过更早墙的
    // 墙体、与它的轮廓交叉(用户反馈"最后一条轮廓与前两条还有交叉")：接头 J 在中线上、而切点在更早墙的侧边上，
    // 两点之间的那块正是更早墙的本体。
    // 被切的是外偏移或内偏移都一样处理(本弧可能朝接头轮廓面的任一侧鼓出)。
    if (outerDeep && !innerDeep) {
      const kept = steppedKeepOther(outerDeep, true);
      if (kept) return kept;
    }
    if (!outerDeep && innerDeep) {
      const kept = steppedKeepOther(innerDeep, false);
      if (kept) return kept;
    }
    const leftDeep = mainOuterIsLeft ? outerDeep : innerDeep;
    const rightDeep = mainOuterIsLeft ? innerDeep : outerDeep;
    if (leftDeep && rightDeep && leftDeep.wallId !== rightDeep.wallId) {
      const cps = intersectCommonShape(extendSeg(leftDeep.seg), extendSeg(rightDeep.seg));
      const corner = cps && cps.length ? getNearestPoint(cps, junction) : null;
      // 距离闸：并集角必须落在两切点的端盖尺度内(≤2×较远切点到接头的距离、且不小于墙厚量级)。
      // 本弧与某条更早墙相切、平口贴齐时，两条偏移边只在接头处相掠——两「切点」其实就是本弧的偏移端点
      // (离接头≈半墙厚、并非真被切进)，但分属两条不同的更早墙，若仍插入两切割边的交点会取到相切远处的假角
      // (跨越大弧的远端点)、鼓出巨大凸起。此时应放弃并集角、直接两点直连成平口(即用户要的「平接」)。
      const maxReach =
        Math.max(
          Point2dUtils.distanceTo(leftDeep.point, junction),
          Point2dUtils.distanceTo(rightDeep.point, junction),
          mainWallInfo.thickness
        ) * 2;
      // 距离闸的【例外】：候选并集角若正是「最早两条墙互相斜接出的那个凸角斜接点」(= jointFillSegs 各段的末端)，
      // 它就是并集轮廓上真实存在的顶点(那两条墙的端盖都以它为端点)，多远都该插。两墙在接头成发夹状锐角时这个斜接点
      // 会远到 2×墙厚之外(实测 145.6 > 闸 140)，漏掉它端盖就退化成一条横跨的直线，把那根斜接尖角整根露在本墙里
      // (用户反馈"最后一条隔墙的起点也是没有被裁切")。凹槽侧的斜接点不在 jointFillSegs 里，所以不会被误放进来。
      const isFillCorner =
        !!corner && jointFillSegs.some((f) => Point2dUtils.distanceTo(f.seg.end, corner) <= thresholdValue);
      // 直接存点而非布尔值：下面要用它作端盖中间点，存布尔的话 corner 的非空信息在那里就丢了
      const cornerInReach =
        corner && (isFillCorner || Point2dUtils.distanceTo(corner, junction) <= maxReach) ? corner : null;
      // 切点→并集角这一段走的就是那条 cutter 的轮廓边：它是弧时端盖也应是弧，不能拉成弦(见 alongCutterArc)。
      return markCapArcs(
        cornerInReach ? [leftDeep.point, cornerInReach, rightDeep.point] : [leftDeep.point, rightDeep.point],
        mainWallInfo,
        leftDeep.seg,
        rightDeep.seg
      );
    }
    // 两侧切在同一条 cutter 边上时，两点之间那段同样是那条边(弧则为弧)。
    return markCapArcs(
      buildCap(leftDeep?.point ?? null, rightDeep?.point ?? null),
      mainWallInfo,
      leftDeep?.seg,
      rightDeep?.seg
    );
  };

  // 端盖按 left→right 排列。仅一侧有切点时经过中线接头点 P 成 3 点折线；两侧都有(或都没有)则两点直连。
  function buildCap(left: IShapePoint2d | null, right: IShapePoint2d | null): IShapePoint2d[] | null {
    if (!left && !right) return null;
    const lp = left ?? leftEnd;
    const rp = right ?? rightEnd;
    if (!left !== !right) return [lp, junction, rp];
    return [lp, rp];
  }

  // 相切直隔墙裁弧：本弧(后建)接头连着更早建立的直隔墙，且本弧偏移带确实探入该直墙带内(探入深度>本弧厚)——
  // 说明两墙带在接头处重叠(相切处弧体压进直墙)。此时本弧被该直墙沿其近边裁回，去掉埋进直墙里的那截偏移轮廓；
  // 直墙(先建)保持完整平口。探入用「本弧外偏移弧与直墙两条边线的最深界内交点、沿直墙前进方向的距离」度量：
  // 相切平滑延续时偏移边与直墙带平行、只在接头处相掠(探入≈0)→ 不裁，交回通用逻辑走平口端盖。
  if (earlierStraightSeps.length) {
    const arcPenetratesBand = (straight: IWallInfo): boolean => {
      const s = straight.seg;
      const far = Point2dUtils.equals(s.start, junction) ? s.end : s.start;
      const along = Point2dUtils.getUnitDirection(junction, far);
      let maxDepth = 0;
      for (const edge of [straight.leftSeg, straight.rightSeg]) {
        const hit = crossSeg(mainOuterSeg, edge, deeper);
        if (!hit) continue;
        const depth = projectAlong(hit.point, junction, along);
        if (depth > maxDepth) maxDepth = depth;
      }
      return maxDepth > mainWallInfo.thickness + thresholdValue;
    };
    const cutters = earlierStraightSeps.filter(
      (w) => isTangentShape(mainWallInfo.shape, w.shape) && arcPenetratesBand(w)
    );
    if (cutters.length) return cutByEarlier([...cutters, ...earlierCutterArcs]);

    // 本弧是接头处「第三条及以后」建立的墙(更早相连隔墙≥2)时：一律被更早的墙轮廓切(见下方 cutByEarlier)。
    // 注意曾有一条「inserter 平切」分支：本弧从更早直墙的端点鼓出时，沿【那条直墙的墙厚端盖面】把本弧平切成 2 点平口。
    // 已删除——与所有平切分支同理：本弧的原始端盖过接头、两个偏移端点必然分居那条端盖面两侧，露在开放侧那个墙角是
    // 真实可见的，沿端盖面平切既会把它抹平、又会沿端盖面【延长线】切到面外的幻影点(实测 70 宽的面上切出 -20/+94)。
    // 用户语义："左侧要削平、右侧交点要连接到前面两条的轮廓线上"，即恒为 cutByEarlier 的布尔差阶梯。
    const earlierConnectedCount = earlierStraightSeps.length + earlierArcs.length;

    // 混合接头(既连更早的直隔墙、又是第三条及以后建立的墙、接头浮空)：本弧被「所有更早的墙(弧+直)」轮廓切。
    // 前两条(建立序)互相 miter、后面的被前面轮廓切——「条」须计入更早直隔墙，否则本弧只有 1 条更早弧时会漏走通用逻辑
    // (选到远端幻影点、端盖绕缠)。用通用最深切割：两条偏移边各自被最深(离接头最远、界内)的更早墙边切。
    if (!onRoomWall && earlierConnectedCount >= 2) {
      const cut = cutByEarlier([...earlierCutterArcs, ...earlierStraightSeps]);
      if (cut && cut.length) return cut;
    }
    return null; // 相切延续 / 无探入 / 无插入：交回通用逻辑(与原 hasEarlierStraightSep 一致)
  }

  if (!connectedArcs.length) return null;

  if (onRoomWall) {
    // 接头落在房间墙上：第一条不被切(平口连墙)；其余被所有更早的弧切。
    if (!earlierArcs.length) {
      if (laterArcs.length) {
        const outerE = endOf(mainOuterSeg, type);
        const innerE = endOf(mainInnerSeg, type);
        // 弧与房间墙相切时，平口两端点会跨在墙两侧(外偏移戳出房间外)。此时交回通用逻辑按墙裁剪，
        // 避免平口戳出墙外；只有平口沿墙(两端点都不在墙外侧)时才用平口直连。
        const roomWall = lookup.resolve(onWall).find((w) => w.thickness === 0);
        if (roomWall) {
          const a = roomWall.seg.start;
          const b = roomWall.seg.end;
          const side = (p: IPoint2d) => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
          const so = side(outerE);
          const si = side(innerE);
          if (Math.abs(so) > 1 && Math.abs(si) > 1 && so * si < 0) return null;
        }
        return orderLR(outerE, innerE);
      }
      return null;
    }
    return cutByEarlier(earlierCutterArcs);
  }

  // 接头浮空：前两条(按建立序)互相角平分线斜接；第三条及以后被更早的弧切。
  // 本弧是第一条(无更早弧、也无更早直墙——此处 earlierStraightSeps 必为空)时，miter 对象应是「真正的第二条」，
  // 即建立序最早的那条后建墙(可能是后建直墙，不只是后建弧)——否则第一条弧会与错误的后建弧斜接、与第二条墙(直墙)对不齐。
  // 接头处更早建立的相连厚墙总数(含被排除出 cutter 的弦墙/孪生直墙——如本弧的直径弦墙)。用于判定本弧是不是
  // 「第三条及以后」：只按更早弧计数会把「更早弧 + 本弧的弦直墙 + 本弧」误判成第二条去和更早弧 miter，
  // 得到斜跨接头的对角端盖。应计入所有更早厚墙——≥2 条时本弧是第三条+、被更早轮廓切割而非 miter。
  const earlierConnectedThick = connectedInfos.filter(
    (w) => w.thickness > 0 && lookup.orderOf(w.id) < mainOrder
  ).length;
  let partner: IWallInfo | undefined;
  if (!earlierArcs.length) {
    const laterSeps = [...laterArcs, ...laterStraightSeps];
    partner = laterSeps.length
      ? laterSeps.reduce((a, b) => (lookup.orderOf(a.id) <= lookup.orderOf(b.id) ? a : b))
      : undefined;
  } else if (earlierArcs.length === 1) partner = earlierArcs[0];
  if (partner) {
    // 接头还落在「更早建立的直隔墙 body」上(本弧实为第三条相连墙)、且两弧【不相切】时：不走互相 miter，改按
    // 「后画的被前画的切」——(相切两弧仍走 miter，见 case3 弧弧相切接头)。前画的弧只按自身端盖(交由直墙近边裁平)，
    // 后画的弧被更早的弧切割；后画弧的端盖会顶到前画弧的轮廓上，两弧在接头对接而非各自向上斜接出一个悬空 miter 角。
    // 端点搭在「更早建立的弧隔墙 body」上(与直墙 body 对称)时同理：本弧是搭接、应被那条更早弧沿其近偏移边裁平，
    // 而不是与「后建」的相连弧互相 miter 悬在 body 外侧留缝。仅在两弧【不相切】(成角搭接)时按此处理；相切延续仍走 miter。
    const hasEarlierBodyOnBody = earlierThickOnBody.length > 0;
    if (hasEarlierBodyOnBody && !isTangentShape(mainWallInfo.shape, partner.shape)) {
      if (earlierArcs.length) {
        // 后画的弧：外偏移被更早的弧切(顶点)、内偏移落到直墙近边，两切割边属不同墙 → 三点端盖：
        // 顶点(外偏移∩更早弧) → 转角(更早弧边∩直墙近边) → 内点(内偏移∩直墙近边)，弧沿前墙轮廓贴合、不悬空。
        const straightInfo = earlierThickOnBody.find((info) => !(info.shape instanceof Circle2d));
        const outerDeep = deepestOnSegs(mainOuterSeg, cutterSegsOf(earlierCutterArcs));
        if (straightInfo && straightInfo.shape instanceof Line2d && outerDeep) {
          const apex = mainWallInfo.seg.getMidPoint();
          const nearLine = extendSeg(nearOffsetSegOf(straightInfo, straightInfo.shape, apex));
          const innerPt = getNearestPoint(intersectCommonShape(mainInnerSeg.createCircle(), nearLine) || [], junction);
          const cutterExt = extendSeg(outerDeep.seg);
          const corner = getNearestPoint(intersectCommonShape(cutterExt, nearLine) || [], junction);
          if (innerPt && corner) {
            return mainOuterIsLeft ? [outerDeep.point, corner, innerPt] : [innerPt, corner, outerDeep.point];
          }
        }
        const cut = cutByEarlier(earlierCutterArcs);
        if (cut && cut.length) return cut;
      } else {
        // 先画的弧：不与后画弧 miter，返回自身端盖(两偏移端点)，由直墙近边裁平。
        return orderLR(endOf(mainOuterSeg, type), endOf(mainInnerSeg, type));
      }
    }
    // 只有本弧确是接头「第二条」(仅 1 条更早厚墙)时才与更早弧互相 miter；接头还连着其它更早厚墙(如本弧弦墙、
    // 使本弧成为第三条+)时不 miter，落到下方被更早的弧整体切割。
    if (earlierConnectedThick < 2) {
      const m = miterAgainst(partner);
      return buildCap(m.left, m.right);
    }
  }
  // 第三条及以后被更早的弧切。但此连接方式下，若本弧【内偏移弧与更早弧都算不到交点】(只有外偏移能求到交点)：
  // 强行按外偏移切会得到内外偏移弧度值不一致的端盖(外偏移被拉到某交点、内偏移停在自身端点 → 尖刺)。
  // 此时保持内外偏移弧度值一致，用自身两偏移端点的平口端盖(接头重叠区由更早的弧盖住)。
  // 内弧能算到交点(如 sixArcFan：内弧切到第二条内弧)则为真实重叠，正常按 cutByEarlier 切。
  // 判据：本弧内偏移弧是否与「最早建立的两条弧(接头 miter 基准对)」算得到交点。第三条+ 是被这前两条切的；
  // 若内弧连前两条都算不到交点(只有外弧能)——如本弧朝前两条的反侧散出——则外弧取其与其他墙边界的真实交点，
  // 内弧取「与外弧交点同弧度(同角度)」处的点，得到内外弧度一致的径向端盖(而非各自平口端点、也非内外不一致的尖刺)。
  // 内外偏移同圆心 → 同弧度即同角度：内点 = 圆心 + 内偏移半径 × 单位(外交点−圆心)。
  // 内弧能切到前两条(sixArcFan/夹在中间的 U5aSll)则为实质重叠，正常 cutByEarlier。注意只用前两条判定内弧：
  // 与相邻的其它后画弧的交点不算数。
  const firstTwo = [...earlierCutterArcs].sort((a, b) => lookup.orderOf(a.id) - lookup.orderOf(b.id)).slice(0, 2);
  const innerHit =
    deepestOnSegs(mainInnerSeg, innerCutterSegsOf(firstTwo), INNER_POS) ??
    deepestOnSegs(mainInnerSeg, cutterSegsOf(firstTwo));
  if (!innerHit) {
    const outerDeep = deepestOnSegs(mainOuterSeg, cutterSegsOf(earlierCutterArcs));
    const center = mainWallInfo.center;
    // 内偏移端点根本没被任何更早墙盖住(如它落在相切弧平端盖面的【外侧】、伸在开放空间里)：不能按外偏移的交点弧度
    // 把它一起径向切回去(那会凭空削掉一大截没被任何东西挡住的墙体、端盖与接头之间留出一道缝)。这一侧「保持原有长度」，
    // 端盖沿更早轮廓阶梯折过去(steppedKeepInner)：切点 → 那条切割边在接头的端点 → 接头 J → 自身内偏移端点，
    // 这样本弧一直顶到接头、与更早轮廓贴合。擦面闸放到 0.999(几乎不拦)：这里的替代方案「径向端盖」会把自由那侧
    // 凭空削回去 40~47、在接头处留下可见缺口，细长的阶梯也比留缝好(实测扇形接头 |dot|=0.99 也该走阶梯)。
    if (outerDeep && !buriedByEarlier(endOf(mainInnerSeg, type))) {
      const kept = steppedKeepInner(outerDeep, 0.999);
      if (kept) return kept;
    }
    if (outerDeep && center) {
      const dir = Point2dUtils.getUnitDirection(center, outerDeep.point);
      const innerPt: IShapePoint2d = {
        x: center.x + dir.x * mainInnerSeg.radius,
        y: center.y + dir.y * mainInnerSeg.radius
      };
      return orderLR(outerDeep.point, innerPt);
    }
    // 外弧也算不到交点：退回自身两偏移端点平口。
    return orderLR(endOf(mainOuterSeg, type), endOf(mainInnerSeg, type));
  }
  return cutByEarlier(earlierCutterArcs);
}
